import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Shield, Eye, EyeOff, Users, MapPin, Clock, ExternalLink, Copy,
  Trash2, CopyPlus, MessageSquare, RotateCcw, Search, Archive,
  ArchiveRestore, AlertTriangle, X
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import AnswerViewer from "../components/AnswerViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_SECRET = "reshapers2026"; // Secret passphrase
const PAGE_SIZE = 25;

const STATUS_BADGE = {
  draft:    "bg-[#FFC400] text-black",
  lobby:    "bg-[#00B0FF] text-black",
  live:     "bg-[#00E676] text-black",
  ended:    "bg-[#6B5E7C] text-white",
  archived: "bg-white/10 text-[#B49FCC]",
};

const formatDate = (s) => (s ? new Date(s).toLocaleString() : "—");
const formatRelative = (s) => {
  if (!s) return "—";
  const ms = Date.now() - new Date(s).getTime();
  if (ms < 0) return "just now";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return formatDate(s);
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");

  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, draft: 0, lobby: 0, live: 0, ended: 0, archived: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | draft | lobby | live | ended | archived
  const [sortBy, setSortBy] = useState("recent");

  const [showCodes, setShowCodes] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [viewingAnswers, setViewingAnswers] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // {session} or null
  const [deleteTypedName, setDeleteTypedName] = useState("");
  const [bulkConfirm, setBulkConfirm] = useState(null); // 'archive' | 'unarchive' | 'delete' | null
  const [bulkTyped, setBulkTyped] = useState("");

  // Debounce search input
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);

  // Auth via URL key
  useEffect(() => {
    if (searchParams.get("key") === ADMIN_SECRET) {
      setIsAuthenticated(true);
    }
  }, [searchParams]);

  const buildQuery = useCallback((offset = 0) => {
    const params = new URLSearchParams({ key: ADMIN_SECRET, limit: String(PAGE_SIZE), offset: String(offset) });
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (statusFilter === "archived") {
      params.set("archived_only", "true");
    } else if (statusFilter !== "all") {
      params.set("status", statusFilter);
    }
    if (sortBy) params.set("sort", sortBy);
    return params.toString();
  }, [debouncedSearch, statusFilter, sortBy]);

  const fetchSessions = useCallback(async (resetList = true) => {
    if (resetList) {
      setIsLoading(true);
      setSelectedIds(new Set());
    } else {
      setIsLoadingMore(true);
    }
    try {
      const offset = resetList ? 0 : sessions.length;
      const res = await axios.get(`${API}/admin/sessions?${buildQuery(offset)}`);
      const next = res.data.sessions || [];
      setSessions(resetList ? next : [...sessions, ...next]);
      setTotal(res.data.total || 0);
      setStats(res.data.stats || {});
    } catch (err) {
      console.error(err);
      toast.error("Failed to load sessions");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [buildQuery, sessions]);

  // Refresh whenever filters/sort/search change OR when auth flips
  useEffect(() => {
    if (isAuthenticated) fetchSessions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, debouncedSearch, statusFilter, sortBy]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_SECRET) {
      setIsAuthenticated(true);
      toast.success("Welcome, Admin!");
    } else {
      toast.error("Invalid passphrase");
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const toggleShowCode = (sessionId) => setShowCodes((p) => ({ ...p, [sessionId]: !p[sessionId] }));
  const openSession = (sessionId) => window.open(`/facilitator/${sessionId}`, "_blank");

  const duplicateSession = async (sessionId) => {
    try {
      const res = await axios.post(`${API}/admin/sessions/${sessionId}/duplicate?key=${ADMIN_SECRET}`);
      toast.success(`Duplicated: ${res.data.session.name}`);
      fetchSessions(true);
    } catch (err) {
      toast.error("Failed to duplicate session");
    }
  };

  const resetSession = async (sessionId) => {
    if (!window.confirm("Reset this game? Removes ALL participants and their results (answers, scores, buzzes, team leads). Stations, questions, and team names/avatars are kept. Cannot be undone.")) return;
    try {
      await axios.post(`${API}/sessions/${sessionId}/reset`);
      toast.success("Game reset");
      fetchSessions(true);
    } catch (err) {
      toast.error("Failed to reset game");
    }
  };

  const archiveSession = async (sessionId, archived) => {
    try {
      const action = archived ? "unarchive" : "archive";
      await axios.post(`${API}/admin/sessions/${sessionId}/${action}?key=${ADMIN_SECRET}`);
      toast.success(archived ? "Session restored" : "Session archived");
      fetchSessions(true);
    } catch (err) {
      toast.error("Action failed");
    }
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    if (deleteTypedName.trim() !== confirmDelete.session.name.trim()) {
      toast.error("Type the session name exactly to confirm");
      return;
    }
    try {
      await axios.delete(`${API}/admin/sessions/${confirmDelete.session.id}?key=${ADMIN_SECRET}`);
      toast.success("Session deleted permanently");
      setConfirmDelete(null);
      setDeleteTypedName("");
      fetchSessions(true);
    } catch (err) {
      toast.error("Failed to delete session");
    }
  };

  // Bulk actions
  const performBulk = async () => {
    if (!bulkConfirm || selectedIds.size === 0) return;
    if (bulkConfirm === "delete" && bulkTyped !== "DELETE") {
      toast.error("Type DELETE (uppercase) to confirm");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/sessions/bulk?key=${ADMIN_SECRET}`, {
        action: bulkConfirm,
        ids: Array.from(selectedIds),
      });
      const msg = `${bulkConfirm}: ${res.data.succeeded.length} succeeded${res.data.failed.length ? `, ${res.data.failed.length} failed` : ""}`;
      toast.success(msg);
      setBulkConfirm(null);
      setBulkTyped("");
      setSelectedIds(new Set());
      fetchSessions(true);
    } catch (err) {
      toast.error("Bulk action failed");
    }
  };

  const toggleSelect = (sid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === sessions.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(sessions.map((s) => s.id)));
  };

  const statusChips = useMemo(() => ([
    { key: "all",      label: "All",      count: stats.total },
    { key: "draft",    label: "Draft",    count: stats.draft },
    { key: "lobby",    label: "Lobby",    count: stats.lobby },
    { key: "live",     label: "Live",     count: stats.live },
    { key: "ended",    label: "Ended",    count: stats.ended },
    { key: "archived", label: "Archived", count: stats.archived },
  ]), [stats]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-black">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#8B5CF6] flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Admin Access</h1>
            <p className="text-[#6B5E7C] mt-2">Enter passphrase to continue</p>
          </div>
          <form onSubmit={handleLogin} className="glass-heavy rounded-2xl p-6" data-testid="admin-login-form">
            <Input type="password" placeholder="Enter passphrase" value={password} onChange={(e) => setPassword(e.target.value)} className="input-dark mb-4" autoFocus />
            <Button type="submit" className="w-full btn-primary-gradient">Access Admin Panel</Button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6 bg-black">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#8B5CF6] flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
              <p className="text-sm text-[#6B5E7C]">Manage all workshop sessions</p>
            </div>
          </div>
          <Button onClick={() => navigate("/")} variant="ghost" className="text-[#E2D9F3]">Exit Admin</Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-5" data-testid="admin-stats">
          {statusChips.map((c) => (
            <div key={c.key} className="glass-heavy rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-white" data-testid={`stat-${c.key}`}>{c.count ?? 0}</p>
              <p className="text-xs text-[#B49FCC] capitalize">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="glass-heavy rounded-2xl p-3 mb-4 flex flex-col gap-3" data-testid="admin-toolbar">
          {/* Search + sort row */}
          <div className="flex flex-col md:flex-row gap-2 md:items-center">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B5E7C]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, join code, or facilitator code…"
                className="input-dark pl-10 pr-9"
                data-testid="admin-search-input"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B5E7C] hover:text-white" aria-label="Clear search">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#E91E8C]/50"
              data-testid="admin-sort-select"
            >
              <option value="recent">Most recent</option>
              <option value="oldest">Oldest first</option>
              <option value="name">Name (A → Z)</option>
              <option value="participants">Most participants</option>
            </select>
          </div>

          {/* Status filter chips */}
          <div className="flex flex-wrap gap-2" data-testid="admin-status-chips">
            {statusChips.map((c) => (
              <button
                key={c.key}
                onClick={() => setStatusFilter(c.key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  statusFilter === c.key
                    ? "bg-[#E91E8C] border-[#E91E8C] text-white"
                    : "bg-white/5 border-white/10 text-[#E2D9F3] hover:bg-white/10"
                }`}
                data-testid={`filter-${c.key}`}
              >
                {c.label} <span className="opacity-70">({c.count ?? 0})</span>
              </button>
            ))}
          </div>

          {/* Bulk action bar */}
          {selectedIds.size > 0 && (
            <div className="bg-[#E91E8C]/10 border border-[#E91E8C]/30 rounded-xl px-3 py-2 flex flex-wrap items-center gap-2" data-testid="admin-bulk-bar">
              <span className="text-sm text-white" data-testid="bulk-selection-count">
                {selectedIds.size} selected
              </span>
              <div className="flex-1" />
              <Button size="sm" variant="outline" className="btn-ghost" onClick={() => setBulkConfirm("archive")} data-testid="bulk-archive-btn">
                <Archive className="w-4 h-4 mr-1" /> Archive
              </Button>
              {statusFilter === "archived" && (
                <Button size="sm" variant="outline" className="btn-ghost text-[#00E676]" onClick={() => setBulkConfirm("unarchive")} data-testid="bulk-unarchive-btn">
                  <ArchiveRestore className="w-4 h-4 mr-1" /> Restore
                </Button>
              )}
              <Button size="sm" className="bg-[#FF1744] hover:bg-[#FF1744]/80" onClick={() => setBulkConfirm("delete")} data-testid="bulk-delete-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
              <Button size="sm" variant="ghost" className="text-[#E2D9F3]" onClick={() => setSelectedIds(new Set())}>Clear</Button>
            </div>
          )}
        </div>

        {/* Sessions List */}
        <div className="space-y-3" data-testid="admin-sessions-list">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-[#E91E8C] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12 text-[#6B5E7C]" data-testid="admin-empty-state">
              {debouncedSearch || statusFilter !== "all" ? "No sessions match your filters" : "No sessions found"}
            </div>
          ) : (
            <>
              {/* Select-all row */}
              <div className="flex items-center gap-2 px-3 py-1 text-xs text-[#6B5E7C]">
                <input
                  type="checkbox"
                  checked={selectedIds.size > 0 && selectedIds.size === sessions.length}
                  ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size !== sessions.length; }}
                  onChange={toggleSelectAll}
                  className="accent-[#E91E8C] w-4 h-4 cursor-pointer"
                  data-testid="admin-select-all"
                  aria-label="Select all visible sessions"
                />
                <span>Showing {sessions.length} of {total}{statusFilter !== "all" || debouncedSearch ? " matching" : ""}</span>
              </div>

              {sessions.map((session) => {
                const isArchived = !!session.archived_at;
                const status = isArchived ? "archived" : session.status;
                return (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`glass-heavy rounded-xl p-4 ${selectedIds.has(session.id) ? "ring-1 ring-[#E91E8C]/50" : ""}`}
                    data-testid={`admin-session-row-${session.id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(session.id)}
                          onChange={() => toggleSelect(session.id)}
                          className="accent-[#E91E8C] w-4 h-4 mt-1.5 cursor-pointer flex-shrink-0"
                          data-testid={`select-${session.id}`}
                          aria-label={`Select ${session.name}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <h3 className="text-lg font-semibold text-white truncate">{session.name}</h3>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_BADGE[status] || STATUS_BADGE.draft}`} data-testid={`status-${session.id}`}>
                              {status}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                            <span className="flex items-center gap-1.5 text-[#E2D9F3]">
                              <Users className="w-3.5 h-3.5" /> {session.participant_count} ppl
                            </span>
                            <span className="flex items-center gap-1.5 text-[#E2D9F3]">
                              <MessageSquare className="w-3.5 h-3.5" /> {session.answer_count} answers
                            </span>
                            <span className="flex items-center gap-1.5 text-[#E2D9F3]">
                              <MapPin className="w-3.5 h-3.5" /> {session.num_groups}t / {session.num_stations}s
                            </span>
                            <span className="flex items-center gap-1.5 text-[#E2D9F3]" title={formatDate(session.last_activity_at)}>
                              <Clock className="w-3.5 h-3.5" /> {formatRelative(session.last_activity_at)}
                            </span>
                          </div>

                          <div className="mt-2 flex flex-wrap gap-3 text-xs">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[#6B5E7C]">Join:</span>
                              <code className="text-[#CCFF00] font-mono">{session.join_code}</code>
                              <button onClick={() => copyToClipboard(session.join_code, "Join code")} className="text-[#6B5E7C] hover:text-white">
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[#6B5E7C]">Facilitator:</span>
                              {showCodes[session.id] ? (
                                <code className="text-[#E91E8C] font-mono">{session.facilitator_code}</code>
                              ) : (
                                <code className="text-[#E91E8C] font-mono">••••••••</code>
                              )}
                              <button onClick={() => toggleShowCode(session.id)} className="text-[#6B5E7C] hover:text-white">
                                {showCodes[session.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                              </button>
                              <button onClick={() => copyToClipboard(session.facilitator_code, "Facilitator code")} className="text-[#6B5E7C] hover:text-white">
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5 lg:flex-nowrap">
                        <Button onClick={() => setViewingAnswers(session)} variant="outline" className="btn-ghost" size="sm" data-testid={`view-answers-${session.id}`}>
                          <MessageSquare className="w-4 h-4 mr-1" /> Answers
                        </Button>
                        <Button onClick={() => duplicateSession(session.id)} variant="outline" className="btn-ghost" size="sm" data-testid={`duplicate-session-${session.id}`}>
                          <CopyPlus className="w-4 h-4 mr-1" /> Duplicate
                        </Button>
                        <Button onClick={() => resetSession(session.id)} variant="outline" className="btn-ghost text-[#FF9800]" size="sm" data-testid={`reset-session-${session.id}`}>
                          <RotateCcw className="w-4 h-4 mr-1" /> Reset
                        </Button>
                        {isArchived ? (
                          <Button onClick={() => archiveSession(session.id, true)} variant="outline" className="btn-ghost text-[#00E676]" size="sm" data-testid={`unarchive-${session.id}`}>
                            <ArchiveRestore className="w-4 h-4 mr-1" /> Restore
                          </Button>
                        ) : (
                          <Button onClick={() => archiveSession(session.id, false)} variant="outline" className="btn-ghost" size="sm" data-testid={`archive-${session.id}`}>
                            <Archive className="w-4 h-4 mr-1" /> Archive
                          </Button>
                        )}
                        <Button onClick={() => { setConfirmDelete({ session }); setDeleteTypedName(""); }} size="sm" className="bg-[#FF1744]/15 hover:bg-[#FF1744]/30 text-[#FF1744]" data-testid={`delete-${session.id}`}>
                          <Trash2 className="w-4 h-4 mr-1" /> Delete
                        </Button>
                        <Button onClick={() => openSession(session.id)} className="btn-primary-gradient" size="sm" data-testid={`open-${session.id}`}>
                          <ExternalLink className="w-4 h-4 mr-1" /> Open
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}

              {sessions.length < total && (
                <div className="flex justify-center pt-2">
                  <Button onClick={() => fetchSessions(false)} disabled={isLoadingMore} variant="outline" className="btn-ghost" data-testid="admin-load-more">
                    {isLoadingMore ? "Loading…" : `Load more (${total - sessions.length} remaining)`}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>

      {/* Answers Dialog */}
      <Dialog open={!!viewingAnswers} onOpenChange={() => setViewingAnswers(null)}>
        <DialogContent className="glass-heavy border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Answers: {viewingAnswers?.name}</DialogTitle>
          </DialogHeader>
          {viewingAnswers?.stations && (
            <AnswerViewer sessionId={viewingAnswers.id} stations={viewingAnswers.stations} />
          )}
        </DialogContent>
      </Dialog>

      {/* Single-row Delete confirmation (type-name to confirm) */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) { setConfirmDelete(null); setDeleteTypedName(""); } }}>
        <DialogContent className="glass-heavy border-white/10 max-w-md" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#FF1744]" /> Permanently delete session?
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#E2D9F3] text-sm py-1">
            This permanently removes <strong className="text-white">{confirmDelete?.session.name}</strong> and all its participants, answers, notes, completion events, and team moves.
            <br /><span className="text-[#FF9494]">This cannot be undone.</span>
          </p>
          <p className="text-xs text-[#B49FCC] mt-2">
            Type the session name <code className="text-white bg-white/5 px-1 rounded">{confirmDelete?.session.name}</code> below to confirm.
          </p>
          <Input
            value={deleteTypedName}
            onChange={(e) => setDeleteTypedName(e.target.value)}
            className="input-dark mt-2"
            placeholder="Session name"
            autoFocus
            data-testid="delete-confirm-input"
          />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => { setConfirmDelete(null); setDeleteTypedName(""); }} className="text-[#E2D9F3]">Cancel</Button>
            <Button
              onClick={performDelete}
              disabled={!confirmDelete || deleteTypedName.trim() !== confirmDelete.session.name.trim()}
              className="bg-[#FF1744] hover:bg-[#FF1744]/80 text-white disabled:opacity-40"
              data-testid="delete-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete forever
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk confirmation dialog */}
      <Dialog open={!!bulkConfirm} onOpenChange={(open) => { if (!open) { setBulkConfirm(null); setBulkTyped(""); } }}>
        <DialogContent className="glass-heavy border-white/10 max-w-md" data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {bulkConfirm === "delete" ? <AlertTriangle className="w-5 h-5 text-[#FF1744]" /> : <Archive className="w-5 h-5 text-[#8B5CF6]" />}
              {bulkConfirm === "delete"
                ? `Delete ${selectedIds.size} session${selectedIds.size === 1 ? "" : "s"} forever?`
                : bulkConfirm === "unarchive"
                ? `Restore ${selectedIds.size} session${selectedIds.size === 1 ? "" : "s"}?`
                : `Archive ${selectedIds.size} session${selectedIds.size === 1 ? "" : "s"}?`}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#E2D9F3] text-sm py-1">
            {bulkConfirm === "delete"
              ? <>This permanently removes every selected session and all related participants, answers, notes, and team moves. <span className="text-[#FF9494]">Cannot be undone.</span></>
              : bulkConfirm === "unarchive"
              ? "Selected archived sessions will be restored to their original status."
              : "Selected sessions will be archived (hidden from the default list, no data loss)."}
          </p>
          {bulkConfirm === "delete" && (
            <>
              <p className="text-xs text-[#B49FCC] mt-2">Type <code className="text-white bg-white/5 px-1 rounded">DELETE</code> to confirm.</p>
              <Input
                value={bulkTyped}
                onChange={(e) => setBulkTyped(e.target.value)}
                className="input-dark mt-2"
                placeholder="DELETE"
                autoFocus
                data-testid="bulk-delete-confirm-input"
              />
            </>
          )}
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => { setBulkConfirm(null); setBulkTyped(""); }} className="text-[#E2D9F3]">Cancel</Button>
            <Button
              onClick={performBulk}
              disabled={bulkConfirm === "delete" && bulkTyped !== "DELETE"}
              className={bulkConfirm === "delete" ? "bg-[#FF1744] hover:bg-[#FF1744]/80 text-white disabled:opacity-40" : "btn-primary-gradient"}
              data-testid="bulk-confirm-btn"
            >
              {bulkConfirm === "delete" ? "Delete forever" : bulkConfirm === "unarchive" ? "Restore" : "Archive"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
